"use client";

/*
  Josh Storer
  App: Wedding App
  File: components/WeddingDecor.tsx
  Component: WeddingDecor
  Version: v1.00
*/

import React from "react";

export function WeddingCelebrationBurst() {
  const pieces = Array.from({ length: 22 }, (_, i) => i);

  return (
    <div className="pointer-events-none fixed inset-x-0 bottom-0 z-40 flex justify-center overflow-hidden">
      <style jsx>{`
        @keyframes burstRise {
          0% {
            transform: translate3d(0, 4vh, 0) scale(0.9) rotate(0deg);
            opacity: 0;
          }
          12% {
            opacity: 0.9;
          }
          100% {
            transform: translate3d(var(--drift-x), -82vh, 0) scale(1.08)
              rotate(var(--spin));
            opacity: 0;
          }
        }
      `}</style>

      <div className="relative h-0 w-[420px] max-w-full">
        {pieces.map((piece) => {
          const size = 6 + (piece % 4) * 2;
          const left = 48 + ((piece % 6) - 3) * 4;
          const delay = `${(piece % 8) * 0.28}s`;
          const duration = `${4.6 + (piece % 5) * 0.45}s`;
          const driftX = `${((piece % 2 === 0 ? 1 : -1) * (30 + (piece % 6) * 10))}px`;
          const spin = `${piece % 2 === 0 ? 260 : -260}deg`;
          const colors = [
            "rgba(202,162,58,0.95)",
            "rgba(255,255,255,0.95)",
            "rgba(31,78,121,0.85)",
            "rgba(148,163,184,0.85)",
          ];

          return (
            <span
              key={piece}
              className="absolute bottom-0 rounded-full"
              style={{
                left: `${left}%`,
                width: `${size}px`,
                height: `${size * 1.6}px`,
                background: colors[piece % colors.length],
                boxShadow: "0 0 10px rgba(255,255,255,0.18)",
                animation: `burstRise ${duration} ease-out ${delay} infinite`,
                ["--drift-x" as string]: driftX,
                ["--spin" as string]: spin,
              }}
            />
          );
        })}
      </div>
    </div>
  );
}

function RoseBloom({
  className = "",
  color = "#1f4e79",
  inner = "#173d61",
  size = 16,
}: {
  className?: string;
  color?: string;
  inner?: string;
  size?: number;
}) {
  return (
    <div
      className={`absolute rounded-full ${className}`}
      style={{
        width: size,
        height: size,
        background: color,
        boxShadow: "inset 0 0 0 4px rgba(255,255,255,0.08)",
      }}
    >
      <div
        className="absolute rounded-full"
        style={{
          inset: "24%",
          border: `2px solid ${inner}`,
        }}
      />
      <div
        className="absolute rounded-full"
        style={{
          inset: "38%",
          border: `2px solid ${inner}`,
        }}
      />
    </div>
  );
}

function Leaf({
  className = "",
  color = "rgba(148,163,184,0.72)",
}: {
  className?: string;
  color?: string;
}) {
  return (
    <div
      className={`absolute h-4 w-8 rounded-full ${className}`}
      style={{ background: color }}
    />
  );
}

export function RoseCorner({ className = "" }: { className?: string }) {
  return (
    <div className={`pointer-events-none absolute ${className}`}>
      <div className="relative h-44 w-44 sm:h-52 sm:w-52">
        <div className="absolute left-5 top-5 h-32 w-32 border border-[#caa23a]" />
        <div className="absolute left-9 top-9 h-32 w-32 border border-[#d9b24d]/75" />

        <RoseBloom
          className="left-1 top-3"
          color="#1f4e79"
          inner="#173d61"
          size={62}
        />
        <RoseBloom
          className="left-14 top-0"
          color="#2f67aa"
          inner="#28588e"
          size={54}
        />
        <RoseBloom
          className="left-24 top-12"
          color="#173d61"
          inner="#102c47"
          size={46}
        />
        <RoseBloom
          className="left-10 top-20"
          color="#3f78ba"
          inner="#2f67aa"
          size={48}
        />
        <RoseBloom
          className="left-28 top-24"
          color="#1f4e79"
          inner="#173d61"
          size={56}
        />

        <Leaf className="left-28 top-5 rotate-12" />
        <Leaf className="left-35 top-17 -rotate-12" />
        <Leaf className="left-2 top-32 rotate-45" />
        <Leaf className="left-33 top-34 -rotate-45" />
      </div>
    </div>
  );
}

export function GoldDivider() {
  return (
    <div className="flex items-center justify-center gap-4 py-2">
      <span className="h-px w-16 bg-[#caa23a]" />
      <span className="text-lg text-[#caa23a]">✦</span>
      <span className="h-px w-16 bg-[#caa23a]" />
    </div>
  );
}
